import type { PhxLambdaV1Config } from '../types/phx-lambda-v1';

/** Type guard for a plain (non-array) object. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Fields that exist on the phx-api Lambda shape but NOT in the terraform-aws-lambda v5 schema.
 * `snap_alias` is removed (v5 creates the SnapStart alias automatically), so it is always dropped.
 */
const REMOVED_KEYS = ['snap_alias'] as const;

/**
 * Load/normalize a raw config.json into a phx-lambda-v1 object for the editor: coerce to an object,
 * stamp the template name, and drop fields that no longer exist in the v5 schema. A single config.json
 * is exactly one standalone Lambda — there is no endpoint/authorizer grouping and no `defaults` block.
 */
export function normalizePhxLambdaV1Config(raw: unknown): PhxLambdaV1Config {
  const config = (isPlainObject(raw) ? structuredClone(raw) : {}) as PhxLambdaV1Config;
  const rec = config as Record<string, unknown>;
  for (const key of REMOVED_KEYS) {
    delete rec[key];
  }
  config.template_name = 'phx-lambda-v1';
  return config;
}

/** Drop empty-value entries (optional) and then the wrapper if the map ends up empty. */
function pruneMap(rec: Record<string, unknown>, key: string, dropEmptyValues = false): void {
  const map = rec[key];
  if (!isPlainObject(map)) return;
  if (dropEmptyValues) {
    for (const k of Object.keys(map)) {
      if (map[k] === '' || map[k] === undefined || map[k] === null) delete map[k];
    }
  }
  if (Object.keys(map).length === 0) delete rec[key];
}

/** Remove empty arrays from an object in place. */
function pruneEmptyArrays(obj: Record<string, unknown>): void {
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (Array.isArray(v) && v.length === 0) delete obj[key];
  }
}

/**
 * Serialize/minimize a phx-lambda-v1 config for writing back to GitHub. Behaviour:
 * - drops `snap_alias` (removed in v5) and empty strings / arrays / objects (each equals the module default);
 * - strips LMI-incompatible fields (snapstart, secrets layer, vpc_config) when a capacity provider is set;
 * - is a near-passthrough otherwise, since config.json mirrors the terraform-aws-lambda v5 variables.
 *
 * Idempotent (format(format(x)) === format(x)) and does not mutate its input.
 */
export function formatPhxLambdaV1Config(input: PhxLambdaV1Config): PhxLambdaV1Config {
  const config = normalizePhxLambdaV1Config(input);
  const rec = config as Record<string, unknown>;

  // vpc_config (was sg_ids): drop empty security_group_ids / subnet_ids, then the empty wrapper.
  if (isPlainObject(rec.vpc_config)) {
    const vpc = rec.vpc_config as Record<string, unknown>;
    pruneEmptyArrays(vpc);
    if (Object.keys(vpc).length === 0) delete rec.vpc_config;
  }

  // datadog (was datadog_lambda_apm_enabled): drop empty sub-values, then the wrapper when nothing is set.
  if (isPlainObject(rec.datadog)) {
    const dd = rec.datadog as Record<string, unknown>;
    for (const k of Object.keys(dd)) {
      if (dd[k] === '' || dd[k] === undefined || dd[k] === null) delete dd[k];
    }
    if (Object.keys(dd).length === 0) delete rec.datadog;
  }

  // events (async destinations): drop blank on_success/on_failure, then the empty wrapper.
  if (isPlainObject(rec.events)) {
    const ev = rec.events as Record<string, unknown>;
    for (const k of Object.keys(ev)) {
      if (ev[k] === '' || ev[k] === undefined || ev[k] === null) delete ev[k];
    }
    if (Object.keys(ev).length === 0) delete rec.events;
  }

  // capacity_provider is the env-agnostic provider NAME. A non-empty string marks the lambda as LMI;
  // a blank/whitespace value is pruned (equals "not LMI", the module default).
  let isLmi = false;
  if (typeof rec.capacity_provider === 'string') {
    if (rec.capacity_provider.trim() === '') {
      delete rec.capacity_provider;
    } else {
      isLmi = true;
    }
  }

  // LMI (Lambda Managed Instances) lambdas do not support SnapStart, the Parameters & Secrets
  // extension layer, or VPC settings — strip them so incompatible config is never emitted.
  if (isLmi) {
    delete rec.snapstart;
    delete rec.param_and_secrets_ext_enabled;
    delete rec.vpc_config;
  }

  // Maps: drop empty-value entries then the empty wrapper.
  pruneMap(rec, 'environment_variables', true);

  // IAM is name lists now (not a name->arn map): iam_policy_names / iam_policy_names_custom are
  // plain string arrays (same shape as phx-api). The top-level empty-array prune below drops them
  // when empty (like grant_invoke_lambda_arns), so each equals the default of "no extra policies".

  // Top-level empties: '' / [] / {} each equal the module default, so removing them is behaviour-preserving.
  // (template_name is re-stamped by normalize, so a later delete here is harmless — it is restored on load.)
  for (const key of Object.keys(rec)) {
    if (key === 'template_name') continue;
    const v = rec[key];
    if (v === '' || v === undefined || v === null) {
      delete rec[key];
    } else if (Array.isArray(v) && v.length === 0) {
      delete rec[key];
    } else if (isPlainObject(v) && Object.keys(v).length === 0) {
      delete rec[key];
    }
  }

  return config;
}
